import { useEffect, useState } from "react";

import { Dashboard } from "./pages/Dashboard";
import { ErrorPanel } from "./components/ErrorPanel";
import { getAuthToken, getContext } from "./lib/teams";
import { resolveLanguage, t, type Language } from "./lib/i18n";
import { startThemeSync } from "./lib/theme";
import {
  resolveTenant,
  UnknownTenantError,
  type TenantEntry,
} from "./lib/tenant";

type BootState =
  | { kind: "booting" }
  | { kind: "unknown_tenant" }
  | {
      kind: "ready";
      tenant: TenantEntry;
      language: Language;
      channelName: string | null;
    };

/** Bootstrap surface — initializes Teams SDK, resolves the per-tenant
 * backend, then renders the Dashboard. Stays slim so the surface area
 * exposed to the Teams init path is minimal. */
export default function App(): JSX.Element {
  const [boot, setBoot] = useState<BootState>({ kind: "booting" });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const ctx = await getContext();
      if (!ctx.tenantId) {
        if (!cancelled) setBoot({ kind: "unknown_tenant" });
        return;
      }
      try {
        const tenant = await resolveTenant(ctx.tenantId);
        const language = resolveLanguage(tenant.language ?? ctx.locale);
        if (!cancelled)
          setBoot({
            kind: "ready",
            tenant,
            language,
            channelName: ctx.channelName,
          });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof UnknownTenantError) {
          setBoot({ kind: "unknown_tenant" });
          return;
        }
        throw err;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Teams theme → `data-theme` on <html> (design review finding 2). Runs
  // independently of the tenant boot: an unknown-tenant or failed-boot
  // surface still has to render on the right ground. The disposer only
  // matters in the no-Teams-host fallback, where an OS media listener is
  // installed.
  useEffect(() => {
    let dispose: (() => void) | null = null;
    let cancelled = false;
    startThemeSync().then((d) => {
      if (cancelled) {
        d();
        return;
      }
      dispose = d;
    });
    return () => {
      cancelled = true;
      dispose?.();
    };
  }, []);

  // A1 (WCAG 3.1.1/3.1.2) — keep `<html lang>` in sync with the resolved
  // tenant language at runtime. index.html ships `lang="pt-AO"`; an EN
  // tenant must flip it so screen readers announce content correctly.
  useEffect(() => {
    if (boot.kind === "ready") {
      document.documentElement.lang = boot.language;
    }
  }, [boot]);

  if (boot.kind === "booting") {
    return (
      <main className="flex min-h-screen items-center justify-center p-4">
        <p className="text-sm text-kwanza-inkSoft" role="status">
          {t("loading", "pt-AO")}
        </p>
      </main>
    );
  }

  if (boot.kind === "unknown_tenant") {
    return (
      <main className="flex min-h-screen items-center justify-center p-4">
        <ErrorPanel
          titleKey="error.tenant_unknown.title"
          bodyKey="error.tenant_unknown.body"
          language="pt-AO"
        />
      </main>
    );
  }

  return (
    // 1400px is the design system's content max-width. The old `max-w-6xl`
    // (1152px) capped the tab at a single column's worth of room, which is
    // half of why the wide Overview had nowhere to go (design review
    // finding 9).
    <main className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 p-4 sm:p-6">
      <Dashboard
        tenant={boot.tenant}
        language={boot.language}
        getAuthToken={getAuthToken}
        channelName={boot.channelName}
      />
    </main>
  );
}
